import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import useModal from "../components/modal/useModal";
import ApiDatabase from "../server";
import { dateShortText2 } from "../utils/Date";
import UseContext from "./useContext";

/** Clé YYYY-MM-DD pour comparer une date mission et le paramètre d’URL malgré formats ISO différents. */
function missionDateCalendarKey(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") return value.split("T")[0];
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
}

const isOpenEndedMission = (detail) =>
  detail?.contractType === "cdi" ||
  Boolean(detail?.dates?.start && !detail?.dates?.end);

const getMissionStartDate = (detail) => {
  if (!detail?.dates?.start) return null;
  const start = new Date(detail.dates.start);
  start.setUTCHours(0, 0, 0, 0);
  return start;
};

const getCalendarKey = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const calendarKeyToUtcDate = (key) => {
  const [year, month, day] = key.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const getMonday = (date) => {
  const dateKey =
    date instanceof Date ? getCalendarKey(date) : String(date).split("T")[0];
  const d = calendarKeyToUtcDate(dateKey);

  const dayOfWeek = d.getUTCDay();
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  d.setUTCDate(d.getUTCDate() - diff);
  return d;
};

const createEmptyMissionDay = (date, detail) => ({
  _id: detail?._id,
  date: new Date(date),
  markups: [],
  pause: "",
  totalHours: 0,
  "totalHoursNight5%": 0,
  "totalHoursNight20%": 0,
  idTimeSheet: null,
  isFlexibilityDate: false,
});

const mapPersistedMarkupToMissionDay = (markup) => ({
  date: markup.date,
  markups: (markup.periods || []).map((period) => period.hours),
  pause: markup.pause ?? "",
  totalHours: markup.totalHours ?? "",
  "totalHoursNight5%": markup["totalHoursNight5%"] ?? "",
  "totalHoursNight20%": markup["totalHoursNight20%"] ?? "",
  idTimeSheet: markup.idTimeSheet || null,
  isFlexibilityDate: false,
});

const mergeMissionDaysByDate = (...missionDayLists) => {
  const byDate = new Map();
  for (const missionDays of missionDayLists) {
    for (const missionDay of missionDays || []) {
      byDate.set(missionDateCalendarKey(missionDay.date), missionDay);
    }
  }
  return Array.from(byDate.values());
};

const buildOpenEndedWeek = (weekDate, detail, persistedDays) => {
  const missionStart = getMissionStartDate(detail);
  if (!missionStart) return [];

  const weekStart = getMonday(weekDate);
  const byDate = new Map(
    (persistedDays || []).map((day) => [missionDateCalendarKey(day.date), day])
  );
  const week = [];

  for (let i = 0; i < 7; i++) {
    const currentDate = new Date(weekStart);
    currentDate.setUTCDate(weekStart.getUTCDate() + i);
    if (currentDate < missionStart) continue;

    const key = getCalendarKey(currentDate);
    week.push(byDate.get(key) || createEmptyMissionDay(currentDate, detail));
  }

  return week;
};

const getInitialOpenEndedDate = (detail, dateParam) => {
  const start = getMissionStartDate(detail);
  if (!start) return new Date();
  const fromUrl = dateParam ? new Date(dateParam) : null;
  if (fromUrl && !Number.isNaN(fromUrl.getTime()) && fromUrl >= start) {
    return fromUrl;
  }
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  return today >= start ? today : start;
};

export function useMissionData() {
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const dateParam = queryParams.get("date");

  const {
    slideOverDataReturn,
    setSlideOverDataReturn,
    setModalData,
    modalDataReturn,
    setModalDataReturn,
    timeCut,
    setTimeCut,
    mission,
    setMission,
    userMission,
    setUserMission,
  } = UseContext();

  const { toggle } = useModal();

  const [isWaitingMission, setIsWaitingMission] = useState(true);
  const [isWaitingUserMission, setIsWaitingUserMission] = useState(true);
  const [missionDetail, setMissionDetail] = useState({});
  const [missionByWeek, setMissionByWeek] = useState([]);
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
  const [persistedMissionDays, setPersistedMissionDays] = useState([]);

  // --- API call for mission
  useEffect(() => {
    if (!id) return;
    setIsWaitingMission(true);
    setMission([]);
    setMissionDetail({});
    setPersistedMissionDays([]);
    setCurrentWeekIndex(0);
    ApiDatabase.getMissionById(
      { id, ...(dateParam ? { date: dateParam } : {}) },
      (data) => {
        setMissionDetail(data.missionDetail);
        const persistedDays = mergeMissionDaysByDate(
          data.missionDetail?.markups?.map(mapPersistedMarkupToMissionDay),
          data.mission || []
        );
        setPersistedMissionDays(persistedDays);
        if (isOpenEndedMission(data.missionDetail)) {
          setMission(
            buildOpenEndedWeek(
              getInitialOpenEndedDate(data.missionDetail, dateParam),
              data.missionDetail,
              persistedDays
            )
          );
        } else {
          setMission(data.mission);
        }
        setTimeCut(data.timeCut);
        setIsWaitingMission(false);
      },
      (err) => {
        console.log(err);
        setIsWaitingMission(false);
      }
    );
  }, [id, dateParam, setMission, setTimeCut]);

  // --- API call for user mission
  useEffect(() => {
    if (!id) return;
    setIsWaitingUserMission(true);
    ApiDatabase.getUserMissionById(
      { id },
      (data) => {
        setUserMission(data.user);
        setIsWaitingUserMission(false);
      },
      (err) => {
        console.log(err);
        setIsWaitingUserMission(false);
      }
    );
  }, [id, setUserMission]);

  // --- Handle slideOver return (timesheet modification, etc.)
  useEffect(() => {
    if (slideOverDataReturn) {
      if (
        slideOverDataReturn.idMission &&
        slideOverDataReturn.date &&
        slideOverDataReturn.timeSheet?.length > 0
      ) {
        const targetKey = missionDateCalendarKey(slideOverDataReturn.date);
        const missionIndex = mission.findIndex(
          (m) => missionDateCalendarKey(m.date) === targetKey
        );
        if (slideOverDataReturn.idMission === id && missionIndex > -1) {
          const newMission = [...mission];
          if (slideOverDataReturn.idTimeSheet) {
            newMission[missionIndex].idTimeSheet =
              slideOverDataReturn.idTimeSheet;
          }
          newMission[missionIndex].markups = slideOverDataReturn.timeSheet;
          newMission[missionIndex].pause = slideOverDataReturn.pause;
          newMission[missionIndex].totalHours = slideOverDataReturn.totalHours;
          newMission[missionIndex]["totalHoursNight5%"] =
            slideOverDataReturn["totalHoursNight5%"];
          newMission[missionIndex]["totalHoursNight20%"] =
            slideOverDataReturn["totalHoursNight20%"];

          setMission(newMission);
          setPersistedMissionDays((prev) => {
            const existingIndex = prev.findIndex(
              (m) => missionDateCalendarKey(m.date) === targetKey
            );
            const updatedDay = newMission[missionIndex];
            if (existingIndex === -1) return [...prev, updatedDay];
            const next = [...prev];
            next[existingIndex] = updatedDay;
            return next;
          });
        }
        setSlideOverDataReturn({});
      }
    }
  }, [slideOverDataReturn, setSlideOverDataReturn, mission, id, setMission]);

  // --- Edit temp worker phone
  const editWorkerPhone = () => {
    toggle("edit_worker_phone");
    setModalData({
      idWorker: userMission.idWorker,
      phone: userMission.phone,
      firstname: userMission.firstname,
      lastname: userMission.lastname,
    });
  };

  // --- Edit temp worker token
  const editWorkerToken = () => {
    toggle("edit_worker_token");
    setModalData({
      idWorker: userMission.idWorker,
    });
  };

  // --- Handle modal return (new phone/token info)
  useEffect(() => {
    if (modalDataReturn?.phone) {
      const userMissionChanges = {
        phone: modalDataReturn.phone,
      };
      if (modalDataReturn.tokenMission) {
        userMissionChanges.tokenMission = modalDataReturn.tokenMission;
      }
      setUserMission({
        ...userMission,
        ...userMissionChanges,
      });
      setModalDataReturn({});
    }
    if (modalDataReturn?.tokenMission) {
      setUserMission({
        ...userMission,
        tokenMission: modalDataReturn.tokenMission,
      });
      setModalDataReturn({});
    }
  }, [modalDataReturn, userMission, setModalDataReturn, setUserMission]);

  // --- Week management
  const groupMissionsByWeeks = (missions) => {
    if (!missions?.length) return [];
    const sorted = [...missions].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    const weeksMap = {};
    sorted.forEach((m) => {
      const monday = getMonday(m.date).toISOString().split("T")[0];

      if (!weeksMap[monday]) {
        weeksMap[monday] = [];
      }
      weeksMap[monday].push(m);
    });
    return Object.values(weeksMap);
  };

  // --- Grouping by weeks
  useEffect(() => {
    if (mission && mission.length) {
      const grouped = groupMissionsByWeeks(mission);
      setMissionByWeek(grouped);
      setCurrentWeekIndex((prevIndex) => {
        // Si l'index actuel est encore dans les limites du nouveau tableau, on le garde
        return prevIndex < grouped.length ? prevIndex : 0;
      });
    } else {
      setMissionByWeek([]);
      setCurrentWeekIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mission]);

  // --- Semaine affichée = celle qui contient le jour du paramètre `date` (recalcul à chaque chargement mission)
  useEffect(() => {
    if (isOpenEndedMission(missionDetail)) return;
    if (!dateParam || !missionByWeek.length) return;
    const target = missionDateCalendarKey(dateParam);
    if (!target) return;
    const targetIndex = missionByWeek.findIndex((week) =>
      week.some((m) => missionDateCalendarKey(m.date) === target)
    );
    if (targetIndex !== -1) {
      setCurrentWeekIndex(targetIndex);
    }
  }, [dateParam, missionByWeek, missionDetail]);

  const getWeekLabel = () => {
    if (!missionByWeek.length) return "";
    const currentWeekMissions = missionByWeek[currentWeekIndex] || [];
    if (currentWeekMissions.length === 0) return "";
    const sorted = [...currentWeekMissions].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );
    const firstDay = dateShortText2(new Date(sorted[0].date));
    const lastDay = dateShortText2(new Date(sorted[sorted.length - 1].date));
    if (sorted.length === 1) {
      return `Semaine du ${firstDay}`;
    }
    return `Semaine du ${firstDay} au ${lastDay}`;
  };

  const canGoPrevOpenEnded = () => {
    if (!isOpenEndedMission(missionDetail)) return false;
    const currentWeek = missionByWeek[currentWeekIndex] || [];
    const firstDate = currentWeek[0]?.date;
    const missionStart = getMissionStartDate(missionDetail);
    if (!firstDate || !missionStart) return false;
    const previousWeekEnd = getMonday(firstDate);
    previousWeekEnd.setUTCDate(previousWeekEnd.getUTCDate() - 1);
    return previousWeekEnd >= missionStart;
  };

  const canGoNextOpenEnded = () =>
    isOpenEndedMission(missionDetail) && missionByWeek.length > 0;

  const canGoPrev = currentWeekIndex > 0 || canGoPrevOpenEnded();
  const canGoNext =
    currentWeekIndex < missionByWeek.length - 1 || canGoNextOpenEnded();

  const goPrevWeek = () => {
    if (currentWeekIndex > 0) {
      setCurrentWeekIndex((prev) => prev - 1);
    } else if (canGoPrevOpenEnded()) {
      const currentWeek = missionByWeek[0] || [];
      const firstDate = currentWeek[0]?.date;
      if (!firstDate) return;
      const previousWeekDate = getMonday(firstDate);
      previousWeekDate.setUTCDate(previousWeekDate.getUTCDate() - 7);
      const previousWeek = buildOpenEndedWeek(
        previousWeekDate,
        missionDetail,
        persistedMissionDays
      );
      setMission((prev) => [...previousWeek, ...prev]);
      setCurrentWeekIndex(0);
    }
  };

  const goNextWeek = () => {
    if (currentWeekIndex < missionByWeek.length - 1) {
      setCurrentWeekIndex((prev) => prev + 1);
    } else if (canGoNextOpenEnded()) {
      const currentWeek = missionByWeek[currentWeekIndex] || [];
      const lastDate = currentWeek[currentWeek.length - 1]?.date;
      if (!lastDate) return;
      const nextWeekDate = getMonday(lastDate);
      nextWeekDate.setUTCDate(nextWeekDate.getUTCDate() + 7);
      const nextWeek = buildOpenEndedWeek(
        nextWeekDate,
        missionDetail,
        persistedMissionDays
      );
      setMission((prev) => [...prev, ...nextWeek]);
      setCurrentWeekIndex((prev) => prev + 1);
    }
  };

  // --- Hour calculations
  const getTotalWorkedHours = () => {
    if (!missionByWeek[currentWeekIndex]) return 0;
    return missionByWeek[currentWeekIndex].reduce((acc, item) => {
      const value = parseFloat(item.totalHours) || 0;
      return acc + value;
    }, 0);
  };

  const getTotalNightHours = () => {
    if (!missionByWeek[currentWeekIndex]) return 0;
    return missionByWeek[currentWeekIndex].reduce((acc, item) => {
      const value5 = parseFloat(item["totalHoursNight5%"]) || 0;
      const value20 = parseFloat(item["totalHoursNight20%"]) || 0;
      return acc + value5 + value20;
    }, 0);
  };

  const getTotalOvertimes = () => {
    if (!missionByWeek[currentWeekIndex]) return 0;
    return Math.max(getTotalWorkedHours() - 35, 0);
  };

  return {
    isWaitingMission,
    isWaitingUserMission,
    missionDetail,
    missionByWeek,
    currentWeekIndex,
    setCurrentWeekIndex,
    canGoPrev,
    canGoNext,
    goPrevWeek,
    goNextWeek,
    getWeekLabel,
    editWorkerPhone,
    editWorkerToken,
    getTotalWorkedHours,
    getTotalNightHours,
    getTotalOvertimes,
    timeCut,
    userMission,
    id,
  };
}
